/* eslint-disable no-console */
import React, { useEffect, useState, useRef, lazy } from "react";
import { Button } from "@vds/buttons";
import { Spacer } from "../../components/core/utility";
import { Col, Grid, Row } from "@vds/grids";
import { Toggle } from "@vds/inputs";
import { handleAddPage, redirectToPrevPage } from "../C4S Decommission - Replacement/utils";
import { HARDWARE_APPROVAL_LIST_COLS } from "../../pages/EditVehicleWTA/HardwareApprovalListColumns";
import { HardwareApprovalListGridComponent } from "../../pages/EditVehicleWTA/HardwareApprovalListGridComponent";
import moment, { max } from "moment";
import _ from "lodash";
import {
  getHardwareApprovalList,
  updateHardwareApprovalAssignees,
  getMyHardwareApprovalRequests,
  navigateToCSPOrder,
} from "../../services/C4S Decommission - Replacement/getHardwareApprovalList";
import { generate } from "shortid";
import { Loader } from "@vds/loaders";
import useOutsideClick from "../../customHook/use-outside-click";
import { Notification } from "@vds/notifications";
import { EditHardwareRequestMetadata } from "./EditHardwareRequestComponent";
import { SLPaginator } from "../../../components/core/Table/PaginatorComponent";
import styled from "styled-components";

export const ASSIGNEE_LIST_COL = [
  { key: "Select Assignee", value: "Select Assignee" },
  { key: "Eric Croucher", value: "Eric Croucher" },
  { key: "Robert Cervantes", value: "Robert Cervantes" },
  { key: "Jason Smith", value: "Jason Smith" },
  { key: "Joel Torres", value: "Joel Torres" },
  { key: "Jorge Valdes-Fauly", value: "Jorge Valdes-Fauly" },
  { key: "Bryan Buford", value: "Bryan Buford" },
  { key: "Luis Navarro", value: "Luis Navarro" },
  { key: "Rafael Lopez", value: "Rafael Lopez" },
  { key: "Michael Herron", value: "Michael Herron" },
];

const itemsPerPageDropDownValues = [
  { label: 10, value: 10 },
  { label: 20, value: 20 },
  { label: 30, value: 30 },
  { label: 40, value: 40 },
  { label: 50, value: 50 },
];

const FullWidthGrid = styled.div`
  width: 100% !important;
  max-width: 100% !important;
`;

export default function HardwareApprovalList(props) {
  let cspSessionData = {};
  try {
    const sessionData = sessionStorage.getItem("CSPDetails");
    const parsedSession = sessionData ? JSON.parse(sessionData) : {};
    cspSessionData = props?.cspData?.data?.data || parsedSession || {};
    cspSessionData.firstName = typeof cspSessionData.firstName === "string" ? cspSessionData.firstName : "";
    cspSessionData.lastName = typeof cspSessionData.lastName === "string" ? cspSessionData.lastName : "";
  } catch (e) {
    cspSessionData = { firstName: "", lastName: "" };
  }
  const [isLoading, setLoader] = useState(false);
  const [hardwareApprovalList, setHardwareApprovalList] = useState([]);
  const [notification, setNotification] = useState(null);
  const notificationTimeoutRef = useRef(null);
  const notificationInteractionRef = useRef(false);
  const [onlyShowMyRequests, setOnlyShowMyRequests] = useState(false);
  const [assigneeUpdates, setAssigneeUpdates] = useState([]);
  const [currentPage, setCurrentPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalRecords, setTotalRecords] = useState(0);
  const handleClickOutside = () => {
    setNotification(null);
  };
  const clickRef = useOutsideClick(handleClickOutside);

  useEffect(() => {
    if (onlyShowMyRequests) {
      const assignee = `${cspSessionData?.firstName} ${cspSessionData?.lastName}`.trim();
      fetchMyRequests(assignee, currentPage, rowsPerPage);
    } else {
      fetchHardwareApprovalRequests(currentPage, rowsPerPage);
    }
    // eslint-disable-next-line
  }, [onlyShowMyRequests, currentPage, rowsPerPage, redirectToPrevPage]);

  useEffect(() => {
    return () => {
      if (notificationTimeoutRef.current) {
        clearTimeout(notificationTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    const handleUserInteraction = () => {
      if (notification && notificationInteractionRef.current) {
        setNotification(null);
        if (notificationTimeoutRef.current) {
          clearTimeout(notificationTimeoutRef.current);
          notificationTimeoutRef.current = null;
        }
      }
    };

    const interactionTimer = setTimeout(() => {
      notificationInteractionRef.current = true;
    }, 1000);

    document.addEventListener("click", handleUserInteraction);
    document.addEventListener("input", handleUserInteraction);
    document.addEventListener("change", handleUserInteraction);

    return () => {
      document.removeEventListener("click", handleUserInteraction);
      document.removeEventListener("input", handleUserInteraction);
      document.removeEventListener("change", handleUserInteraction);
      clearTimeout(interactionTimer);
    };
  }, [notification]);

  const notify = (content, type) => {
    if (notificationTimeoutRef.current) {
      clearTimeout(notificationTimeoutRef.current);
      notificationTimeoutRef.current = null;
    }

    notificationInteractionRef.current = false;

    setNotification(
      <>
        <Notification
          key={generate()}
          type={type}
          title={content}
          fullBleed={true}
          onClose={() => {
            if (notificationTimeoutRef.current) {
              clearTimeout(notificationTimeoutRef.current);
              notificationTimeoutRef.current = null;
            }
            setNotification(null);
          }}
          dismissible={true}
        />
        <br />
      </>
    );

    if (type !== "error") {
      notificationTimeoutRef.current = setTimeout(() => {
        setNotification(null);
        notificationTimeoutRef.current = null;
      }, 5000);
    }
  };

  const fetchHardwareApprovalRequests = async (page = 0, rows = 10) => {
    setLoader(true);
    try {
      const response = await getHardwareApprovalList(page, rows);
      if (response.status === 200 && response?.data?.data) {
        const formatted = response?.data?.data?.requests.map((row) => ({
          ...row,
          requestDate: formatDate(row.requestDate),
          resolvedDate: formatDate(row.resolvedDate),
          assignee: Array.isArray(row.assignee) ? row.assignee : row.assignee ? [row.assignee] : [],
          selectedAssignee:
            Array.isArray(row.assignee) && row.assignee.length > 0 ? row.assignee[0] : typeof row.assignee === "string" ? row.assignee : "",
          shippingAddress: formatShippingAddress(row.shippingAddress) || "",
        }));
        // Sort only by Request (id)
        const sorted = _.orderBy(
          formatted,
          [
            (row) => {
              const num = Number(row.id);
              return isNaN(num) ? String(row.id).toLowerCase() : num;
            },
          ],
          ["desc"]
        );
        setHardwareApprovalList(sorted);
        setTotalRecords(response?.data?.data?.totalRecords || sorted.length);
      } else {
        notify("Unable to fetch hardware approval requests", "error");
      }
    } catch (error) {
      notify("Error fetching hardware approval requests", "error");
    }
    setLoader(false);
  };

  const fetchMyRequests = async (assignee, page = 0, rows = 10) => {
    setLoader(true);
    try {
      const response = await getMyHardwareApprovalRequests(assignee, page, rows);
      if (response.status === 200 && response?.data?.data) {
        const formatted = response?.data?.data?.requests.map((row) => ({
          ...row,
          requestDate: formatDate(row.requestDate),
          resolvedDate: formatDate(row.resolvedDate),
          assignee: Array.isArray(row.assignee) ? row.assignee : row.assignee ? [row.assignee] : [],
          selectedAssignee:
            Array.isArray(row.assignee) && row.assignee.length > 0 ? row.assignee[0] : typeof row.assignee === "string" ? row.assignee : "",
          shippingAddress: formatShippingAddress(row.shippingAddress) || "",
        }));
        const sorted = _.orderBy(
          formatted,
          [
            (row) => {
              const num = Number(row.id);
              return isNaN(num) ? String(row.id).toLowerCase() : num;
            },
          ],
          ["desc"]
        );
        setHardwareApprovalList(sorted);
        setTotalRecords(response?.data?.data?.totalRecords || sorted.length);
      } else {
        notify("Unable to fetch hardware approval requests", "error");
      }
    } catch (error) {
      notify("Error fetching hardware approval requests", "error");
    }
    setLoader(false);
  };

  const editHardwareRequest = (data) => {
    handleAddPage(EditHardwareRequestMetadata.id, {
      data,
    });
  };

  const onHyperLinkClick = (data, column) => {
    if (column === "Request") {
      editHardwareRequest(data);
      return;
    }
    if (column === "Order" && data && data.cspId && String(data.cspId).trim() !== "") {
      navigateToCSPOrder(data.cspId);
      return;
    }
  };

  const handleClick = async () => {
    if (!Array.isArray(assigneeUpdates) || assigneeUpdates.length === 0) {
      notify("No assignee changes to save.", "info");
      return;
    }
    const assigneePayload = assigneeUpdates.reduce((acc, curr) => {
      if (curr.id && curr.assignee) {
        acc[String(curr.id)] = String(curr.assignee);
      }
      return acc;
    }, {});
    const payload = {
      action: "CHANGE_ASSIGNEE",
      assignee: assigneePayload,
      hardwareAgent: `${cspSessionData?.firstName} ${cspSessionData?.lastName}` || "",
    };
    try {
      const response = await updateHardwareApprovalAssignees(payload);
      if (response && response.status === 200) {
        notify("Assignee changes saved successfully.", "success");
        if (onlyShowMyRequests) {
          const assignee = `${cspSessionData?.firstName} ${cspSessionData?.lastName}`.trim();
          fetchMyRequests(assignee, currentPage, rowsPerPage);
        } else {
          fetchHardwareApprovalRequests(currentPage, rowsPerPage);
        }
      } else {
        notify("Failed to save assignee changes.", "error");
      }
    } catch (e) {
      notify("Failed to save assignee changes.", "error");
    }
  };

  // Format date fields for display in the grid
  const formatDate = (dateStr) => {
    if (!dateStr) return "--";
    const date = moment(dateStr);
    if (!date.isValid()) return dateStr;
    return date.format("MMMM D, YYYY");
  };

  const handleAssigneeChange = (rowIndex, newAssignee) => {
    setHardwareApprovalList((prev) => {
      const updatedList = prev.map((row, idx) => (idx === rowIndex ? { ...row, selectedAssignee: newAssignee } : row));
      setAssigneeUpdates((prevUpdates) => {
        const changedRow = updatedList[rowIndex];
        if (!changedRow || !changedRow.id) return prevUpdates;
        const updateObj = { id: changedRow.id, assignee: newAssignee };
        const existingIdx = prevUpdates.findIndex((u) => u.id === changedRow.id);
        if (existingIdx > -1) {
          const updated = [...prevUpdates];
          updated[existingIdx] = updateObj;
          return updated;
        } else {
          return [...prevUpdates, updateObj];
        }
      });
      return updatedList;
    });
  };

  // Helper to format shipping address object as a single string
  const formatShippingAddress = (addressObj) => {
    if (!addressObj || typeof addressObj !== "object") return "";
    const { addressLine1 = "", addressLine2 = "", city = "", state = "", postalCode = "" } = addressObj;
    return [addressLine1, addressLine2, city, state, postalCode].filter((part) => part && part.trim() !== "").join(", ");
  };

  // Prepare dropDownValues for the grid using ASSIGNEE_LIST_COL
  const dropDownValues = React.useMemo(() => {
    return {
      assignee: ASSIGNEE_LIST_COL.map((a) => ({ label: a.key, value: a.value })),
    };
  }, []);

  // Custom cell renderer for Assignee column
  const assigneeCellRenderer = (row, rowIndex) => {
    const selected = row.selectedAssignee || "";
    return (
      <select
        value={selected}
        onChange={(e) => {
          const value = e.target.value;
          handleAssigneeChange(rowIndex, value);
        }}
        style={{ minWidth: 120 }}
      >
        <option value="" disabled>
          Select Assignee
        </option>
        {ASSIGNEE_LIST_COL.map((assignee, i) => (
          <option key={i} value={assignee.value}>
            {assignee.key}
          </option>
        ))}
      </select>
    );
  };

  const wrapCellRenderer = (row, col) => {
    const value = row[col.value];
    if (col.value === "shippingAddress") {
      return <span style={{ whiteSpace: "normal", wordBreak: "break-word" }}>{formatShippingAddress(value)}</span>;
    }
    if (Array.isArray(value)) {
      return <span style={{ whiteSpace: "normal", wordBreak: "break-word" }}>{value.join(", ")}</span>;
    }
    if (typeof value === "object" && value !== null) {
      return <span style={{ whiteSpace: "normal", wordBreak: "break-word" }} />;
    }
    if (typeof value === "string" || typeof value === "number" || typeof value === "boolean") {
      return <span style={{ whiteSpace: "normal", wordBreak: "break-word" }}>{value}</span>;
    }
    return <span style={{ whiteSpace: "normal", wordBreak: "break-word" }} />;
  };

  const columnsWithWrapping = React.useMemo(
    () =>
      HARDWARE_APPROVAL_LIST_COLS.map((col) => {
        if (col.value === "assignee") {
          return {
            ...col,
            cellRenderer: (row, rowIndex) => assigneeCellRenderer(row, rowIndex),
            style: { whiteSpace: "normal", wordBreak: "break-word" },
          };
        }
        if (col.value === "shippingAddress") {
          return {
            ...col,
            cellRenderer: (row) => (
              <span style={{ whiteSpace: "normal", wordBreak: "break-word" }}>{formatShippingAddress(row.shippingAddress)}</span>
            ),
            style: { whiteSpace: "normal", wordBreak: "break-word" },
          };
        }
        return {
          ...col,
          cellRenderer: (row) => wrapCellRenderer(row, col),
          style: { whiteSpace: "normal", wordBreak: "break-word" },
        };
      }),
    [wrapCellRenderer, assigneeCellRenderer]
  );

  const onPageChangeHandler = ({ page, rows } = {}) => {
    setCurrentPage(page);
    setRowsPerPage(rows);
    // Removed: fetchHardwareApprovalRequests(page, rows);
    // The useEffect will handle fetching when currentPage/rowsPerPage change
  };

  const onPageInputChangeHandler = (event, options) => {
    const page = parseInt(event?.target?.value, 10) - 1;
    if (!isNaN(page) && page >= 0) {
      onPageChangeHandler({ page, rows: options?.rows || rowsPerPage });
    }
  };

  return (
    <>
      <Spacer top="10px" />
      <div style={{ position: "relative" }}>
        {isLoading && (
          <div
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: "rgba(255, 255, 255, 0.7)",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              zIndex: 100,
            }}
          >
            <Loader active={true} size="large" />
          </div>
        )}

        <FullWidthGrid>
          <Grid colSizes={{ mobile: 5, tablet: 5, desktop: 2.5 }} style={{ margin: 0, width: "100%", maxWidth: "100%" }}>
            <Row css={{ flexDirection: "row", justifyContent: "start", display: "flex !important", flex: "auto", width: "100%", maxWidth: "100%" }}>
              <Col css={{ flex: 4, display: "flex", flexDirection: "column", paddingLeft: 0, width: "100%" }}>{notification}</Col>
            </Row>
            <Row css={{ flexDirection: "row", justifyContent: "start", display: "flex !important", flex: "auto", width: "100%", maxWidth: "100%" }}>
              <Col css={{ flex: 2, display: "flex", flexDirection: "column", paddingLeft: 0, width: "100%" }}>
                <div
                  style={{
                    color: "#000000",
                    fontFamily: "Verizon NHG eTX, Helvetica, Arial, sans-serif",
                    fontStyle: "bold",
                    fontWeight: "700",
                    fontSize: "20px",
                    lineHeight: "24px",
                    letterSpacing: "0px",
                    textAlign: "left",
                    textTransform: "none",
                    verticalAlign: "middle",
                  }}
                >
                  Hardware Approval
                </div>
              </Col>
              <Col css={{ flex: 2, display: "flex", justifyContent: "flex-end", alignItems: "center", width: "100%" }}>
                <div style={{ display: "flex", alignItems: "center", flexDirection: "row", gap: "0.5rem" }}>
                  <span
                    style={{
                      color: "#000000",
                      fontFamily: "Verizon NHG eTX, Helvetica, Arial, sans-serif",
                      fontStyle: "bold",
                      fontWeight: "700",
                      fontSize: "12px",
                      lineHeight: "16px",
                      letterSpacing: "0px",
                      textAlign: "left",
                      textTransform: "none",
                      verticalAlign: "middle",
                    }}
                  >
                    Only Show My Requests
                  </span>
                  <Toggle
                    value={onlyShowMyRequests}
                    onChange={() => setOnlyShowMyRequests((prev) => !prev)}
                    surface="light"
                    data-testid="only-my-requests-toggle"
                    showText={false}
                    textWeight="regular"
                    textSize="small"
                    textPosition="left"
                    aria-label="Only Show My Requests"
                    style={{ marginRight: 8 }}
                  />
                  <Button
                    style={{ marginLeft: "0.5rem" }}
                    role="button"
                    size="small"
                    variant="secondary"
                    aria-label="saveButton"
                    onClick={handleClick}
                  >
                    Save
                  </Button>
                </div>
              </Col>
            </Row>
            <Row
              css={{
                flexDirection: "row",
                justifyContent: "start",
                display: "flex !important",
                flex: "auto",
                marginTop: "0px",
                width: "100%",
                maxWidth: "100%",
              }}
            >
              <Col css={{ flex: 1, display: "flex", flexDirection: "column", position: "relative", paddingLeft: 0, width: "100%" }}>
                <FullWidthGrid style={{ position: "sticky", top: 0 }}>
                  <HardwareApprovalListGridComponent
                    rows={hardwareApprovalList}
                    columns={columnsWithWrapping}
                    dropDownValues={dropDownValues}
                    onDropDownClick={handleAssigneeChange}
                    hideGlobalFilter={true}
                    hyperLinkClicked={onHyperLinkClick}
                    whiteSpace="normal"
                    itemsPerPageDropDownValues={itemsPerPageDropDownValues}
                    style={{ width: "100%" }}
                  />
                </FullWidthGrid>
              </Col>
            </Row>
            <Row css={{ width: "100%", marginTop: "2px", maxWidth: "100%" }}>
              <Col css={{ width: "100%" }}>
                <SLPaginator
                  itemsPerPageDropDownValues={itemsPerPageDropDownValues}
                  totalRecords={totalRecords}
                  onPageChangeHandler={onPageChangeHandler}
                  onPageInputChangeHandler={onPageInputChangeHandler}
                  first={currentPage * rowsPerPage}
                  rows={rowsPerPage}
                  css={{ width: "100%" }}
                />
              </Col>
            </Row>
          </Grid>
        </FullWidthGrid>
      </div>
    </>
  );
}

export const HardwareApprovalListMetadata = {
  name: "Hardware Approval",
  id: "hardware_approval",
  component: lazy(() => import("./HardwareApprovalList")),
  route: "/hardware_approval",
};
